import React, { useCallback, useState } from "react";
import {
    AlertTriangle,
    Clock,
    FileText,
    Gavel,
    Layers,
    Loader2,
    Scale,
    Timer,
    Wallet,
} from "lucide-react";
import { toast } from "sonner";
import { tahkimMerkez } from "../../../lib/tahkimApi";

const RAPOR_KARTLARI = [
    {
        key: "tam",
        label: "Tam Tahkim Raporu",
        hint: "Tüm bölümler — özet, ön tahkim, tahkim, hakem, ödeme, şirket",
        icon: Layers,
        accent: "border-[#D4AF37]/40 bg-[#D4AF37]/8 hover:border-[#D4AF37]/60",
        featured: true,
    },
    {
        key: "on_tahkim_bekleyen",
        label: "Ön Tahkim Bekleme",
        hint: "Başvuru süresi bekleyen tüm dosyalar",
        icon: Timer,
        accent: "border-sky-500/35 bg-sky-500/8 hover:border-sky-400/50",
    },
    {
        key: "yaklasan",
        label: "Yaklaşan",
        hint: "Hedef tarihe ≤3 gün kalan dosyalar",
        icon: Clock,
        accent: "border-amber-500/35 bg-amber-500/8 hover:border-amber-400/50",
    },
    {
        key: "gecikti",
        label: "Geciken / Hazır",
        hint: "Süresi dolmuş veya bugün geçişe hazır dosyalar",
        icon: AlertTriangle,
        accent: "border-rose-500/35 bg-rose-500/8 hover:border-rose-400/50",
    },
    {
        key: "on_tahkim_aktif",
        label: "Aktif Ön Tahkim",
        hint: "ON_TAHKIM aşamasındaki dosyalar",
        icon: Scale,
        accent: "border-cyan-500/35 bg-cyan-500/8 hover:border-cyan-400/50",
    },
    {
        key: "tahkimde",
        label: "Tahkimde Olanlar",
        hint: "Aktif tahkim sürecindeki tüm dosyalar",
        icon: FileText,
        accent: "border-violet-500/35 bg-violet-500/8 hover:border-violet-400/50",
    },
    {
        key: "hakem",
        label: "Hakem Atananlar",
        hint: "Hakem bilgisi girilmiş dosyalar",
        icon: Gavel,
        accent: "border-indigo-500/35 bg-indigo-500/8 hover:border-indigo-400/50",
    },
    {
        key: "odeme_geciken",
        label: "Ödeme Gecikenler",
        hint: "Son ödeme tarihi geçmiş bekleyen tahsilatlar",
        icon: Wallet,
        accent: "border-orange-500/35 bg-orange-500/8 hover:border-orange-400/50",
    },
    {
        key: "sirket",
        label: "Şirket Dağılımı",
        hint: "Sigorta şirketine göre dosya sayıları",
        icon: FileText,
        accent: "border-emerald-500/35 bg-emerald-500/8 hover:border-emerald-400/50",
    },
];

export default function TahkimRaporPdfPanel({ q = "", filters = {} }) {
    const [busy, setBusy] = useState(null);

    const indir = useCallback(
        async (tur) => {
            setBusy(tur);
        try {
            const params = { tur, ...filters };
            if (q.trim()) params.q = q.trim();
            await tahkimMerkez.exportRaporPdf(params);
            toast.success("PDF rapor indirildi");
        } catch (e) {
            toast.error(e?.message || "PDF rapor oluşturulamadı");
        } finally {
                setBusy(null);
            }
        },
        [filters, q]
    );

    return (
        <section className="space-y-5">
            <div className="rounded-2xl border border-[#D4AF37]/25 bg-gradient-to-r from-[#D4AF37]/10 via-[#121A2F]/80 to-violet-500/8 p-4 md:p-5">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                    <div>
                        <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                            <Layers className="h-5 w-5 text-[#D4AF37]" />
                            Tahkim PDF Rapor Merkezi
                        </h3>
                        <p className="text-xs text-slate-400 mt-1.5 max-w-2xl leading-relaxed">
                            Tam operasyon raporu: tahkimi yaklaşanlar, ön tahkim bekleyenler ve aktif
                            tahkim süreci. Dosya durumu «Kapatıldı» veya aşama «Kapandı» olan kayıtlar
                            rapora dahil edilmez. Üst filtreler rapora yansır.
                        </p>
                    </div>
                    <button
                        type="button"
                        disabled={busy === "tam"}
                        onClick={() => indir("tam")}
                        className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-[#D4AF37]/90 to-amber-600/90 text-[#0B0F19] text-sm font-bold shadow-lg shadow-[#D4AF37]/15 disabled:opacity-50 shrink-0"
                    >
                        {busy === "tam" ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                            <Layers className="h-4 w-4" />
                        )}
                        Tam Rapor PDF (Full)
                    </button>
                </div>
            </div>

            <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3">
                {RAPOR_KARTLARI.filter((r) => !r.featured).map((r) => {
                    const Icon = r.icon;
                    const loading = busy === r.key;
                    return (
                        <button
                            key={r.key}
                            type="button"
                            disabled={!!busy}
                            onClick={() => indir(r.key)}
                            className={`text-left rounded-xl border p-4 transition-all disabled:opacity-50 ${r.accent}`}
                        >
                            <div className="flex items-start justify-between gap-2">
                                <div className="flex items-center gap-2 text-sm font-semibold text-white">
                                    <Icon className="h-4 w-4 shrink-0 opacity-80" />
                                    {r.label}
                                </div>
                                {loading ? (
                                    <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
                                ) : (
                                    <FileText className="h-4 w-4 text-slate-500" />
                                )}
                            </div>
                            <p className="text-[11px] text-slate-400 mt-2 leading-relaxed">{r.hint}</p>
                            <span className="inline-block mt-3 text-[10px] uppercase tracking-wider text-slate-500">
                                PDF indir →
                            </span>
                        </button>
                    );
                })}
            </div>
        </section>
    );
}
